import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ArrowLeft, ArrowRight, ChartColumn, Check, CircleAlert, Copy, ExternalLink, Info, Megaphone, Package, RefreshCw, Settings, Wallet } from 'lucide-react';
import { StorefrontService } from '../../../core/services/storefront.service';
import { StorefrontProducts } from './StorefrontProducts';
import { StorefrontEarnings } from './StorefrontEarnings';
import { StorefrontTraffic } from './StorefrontTraffic';
import { StoreImageUpload } from './StoreImageUpload';
import { StoreField } from './StoreField';
import { StorefrontCashkaroLink } from './StorefrontCashkaroLink';
import { StorefrontCashkaroPerformance } from './StorefrontCashkaroPerformance';
import { BASE_URL_UI } from '../../../config/config';
import { useGoBack } from '../../../modules/shared/hooks/useGoBack';
import { brdPaths } from '../../../modules/brand/routes';
import { notify } from '../../../modules/shared/utils/toast';
import '../../../modules/shared/theme/theme.scss';
import './storefront.scss';

interface Props { UserReducer: any; UserID: any; }

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED'];
const STEPS = ['Store Details', 'Manage Products'];

// Derive a URL-safe slug from the store name (lowercase, hyphen-separated).
const slugify = (s: string) => (s || '')
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

const StorefrontComponent: React.FC<Props> = ({ UserReducer }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [settingsTab, setSettingsTab] = useState(0); // 0 = Products, 1 = Store Settings
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [slugChecking, setSlugChecking] = useState(false);
    const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
    const [isExisting, setIsExisting] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    // Full storefront row (not just the editable form fields) — needed for storefront_id
    // and the cashkaro_campaign_* fields consumed by StorefrontCashkaroLink below.
    const [storefrontRow, setStorefrontRow] = useState<any>(null);

    const [form, setForm] = useState<any>({
        store_name: '', store_slug: '', store_logo: '', store_banner: '',
        store_description: '', website_url: '', default_currency: 'INR',
        logo_file_name: '', logo_file_type: '', logo_file_path: '', logo_storage_size: '',
        banner_file_name: '', banner_file_type: '', banner_file_path: '', banner_storage_size: '',
    });

    const goBack = useGoBack(brdPaths.home());
    const brand_id = UserReducer?.UserData?.brand_id;
    const account_id = UserReducer?.UserData?.account_id;
    const influencer_id = UserReducer?.UserData?.influencer_id;
    const user_id = UserReducer?.UserData?.user_id;

    const refetchStorefront = () => {
        StorefrontService.getMyStorefront(brand_id)
            .then((res: any) => { if (res?.data?.rows?.length) setStorefrontRow(res.data.rows[0]); })
            .catch(() => {});
    };

    useEffect(() => {
        StorefrontService.getMyStorefront(brand_id)
            .then((res: any) => {
                if (res?.data?.rows?.length) {
                    const r = res.data.rows[0];
                    setStorefrontRow(r);
                    setForm({
                        store_name: r.store_name || '',
                        store_slug: r.store_slug || '',
                        store_logo: r.store_logo || '',
                        store_banner: r.store_banner || '',
                        store_description: r.store_description || '',
                        website_url: r.website_url || '',
                        default_currency: r.default_currency || 'INR',
                        logo_file_name: r.logo_file_name || '', logo_file_type: r.logo_file_type || '',
                        logo_file_path: r.logo_file_path || '', logo_storage_size: r.logo_storage_size || '',
                        banner_file_name: r.banner_file_name || '', banner_file_type: r.banner_file_type || '',
                        banner_file_path: r.banner_file_path || '', banner_storage_size: r.banner_storage_size || '',
                    });
                    setIsExisting(true);
                    setActiveStep(1); // already set up — skip to products
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [brand_id]);

    const uploadPath = `accounts/${account_id}/brand/${brand_id}/storefront`;

    const setLogo = (m: any) => setForm((f: any) => ({ ...f, store_logo: m.url, logo_file_name: m.file_name, logo_file_type: m.file_type, logo_file_path: m.file_path, logo_storage_size: m.storage_size }));
    const removeLogo = () => setForm((f: any) => ({ ...f, store_logo: '', logo_file_name: '', logo_file_type: '', logo_file_path: '', logo_storage_size: '' }));
    const setBanner = (m: any) => setForm((f: any) => ({ ...f, store_banner: m.url, banner_file_name: m.file_name, banner_file_type: m.file_type, banner_file_path: m.file_path, banner_storage_size: m.storage_size }));
    const removeBanner = () => setForm((f: any) => ({ ...f, store_banner: '', banner_file_name: '', banner_file_type: '', banner_file_path: '', banner_storage_size: '' }));

    // Check availability of a given slug (debounced via the effect below).
    const checkSlug = (slug: string) => {
        setSlugAvailable(null);
        if (!slug || slug.length < 3) return;
        setSlugChecking(true);
        StorefrontService.checkSlug(slug, brand_id)
            .then((res: any) => setSlugAvailable(res?.data?.available ?? null))
            .catch(() => setSlugAvailable(null))
            .finally(() => setSlugChecking(false));
    };

    // First-time setup: slug auto-follows the store name. For an existing store we
    // never auto-change the slug (it would break already-shared store links) — it
    // only changes when the user clicks the regenerate button.
    useEffect(() => {
        if (isExisting) return;
        const base = slugify(form.store_name);
        setForm((f: any) => (f.store_slug === base ? f : { ...f, store_slug: base }));
        const t = setTimeout(() => checkSlug(base), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.store_name, isExisting]);

    // Regenerate a fresh, likely-unique slug from the current name (+ random suffix).
    const regenerateSlug = () => {
        const base = slugify(form.store_name) || 'store';
        const candidate = `${base}-${Math.random().toString(36).slice(2, 6)}`;
        setForm((f: any) => ({ ...f, store_slug: candidate }));
        checkSlug(candidate);
    };

    const handleSubmit = async () => {
        if (!form.store_name || !form.store_slug) {
            setAlert({ type: 'error', msg: 'Store name and slug are required' });
            return;
        }
        setSaving(true);
        setAlert(null);
        try {
            const res = await StorefrontService.setupStorefront({ ...form, brand_id, account_id, influencer_id, user_id });
            if (res?.status === 1) {
                if (!isExisting) {
                    setIsExisting(true);
                    setActiveStep(1);
                } else {
                    notify.success('Storefront updated');
                }
            } else {
                setAlert({ type: 'error', msg: res?.info || 'Failed to save storefront' });
            }
        } catch {
            setAlert({ type: 'error', msg: 'Something went wrong' });
        } finally {
            setSaving(false);
        }
    };

    const tabs: { label: string; icon: React.ReactNode }[] = [
        { label: 'Products', icon: <Package size={16} /> },
        { label: 'Analytics', icon: <ChartColumn size={16} /> },
        { label: 'Affiliate Sales', icon: <Wallet size={16} /> },
        { label: 'Affiliate Deals', icon: <Megaphone size={16} /> },
        { label: 'Store Settings', icon: <Settings size={16} /> },
    ];
    const dealsDisabledNote = (
        <div className="brd-banner brd-banner--info">
            <Info size={16} />
            <span>Affiliate Deals isn't enabled for your account yet — contact your ClanConnect account manager to get access.</span>
        </div>
    );

    if (loading) {
        return <div className="brd brd-app brd-store"><div className="brd-center"><span className="brd-spinner" /></div></div>;
    }

    const slugHint =
        slugChecking ? 'Checking availability…' :
        slugAvailable === true ? <span className="brd-store__ok"><Check size={12} strokeWidth={3} /> Available</span> :
        slugAvailable === false ? null :
        'Auto-generated from your store name.';

    const saveButton = (
        <button type="button" className="brd-btn brd-btn--primary brd-btn--lg" onClick={handleSubmit}
            disabled={saving || slugAvailable === false} aria-busy={saving}>
            {saving
                ? <><span className="brd-spinner brd-spinner--sm brd-spinner--light" /> Saving…</>
                : isExisting ? <><Check size={18} /> Update Storefront</> : <>Save &amp; Continue <ArrowRight size={18} /></>}
        </button>
    );

    const settingsSections = (
        <>
            {alert && (
                <div className="brd-banner brd-banner--error" role="alert">
                    <CircleAlert size={16} />
                    <span style={{ flex: 1 }}>{alert.msg}</span>
                </div>
            )}
            <section className="brd-section">
                <h2 className="brd-section__title">Store details</h2>
                <StoreField label="Store name" required>
                    <input className="brd-input" value={form.store_name} placeholder="e.g. Glow Naturals" aria-label="Store name"
                        onChange={e => setForm((f: any) => ({ ...f, store_name: e.target.value }))} />
                </StoreField>
                <StoreField label="Store link" required hint={slugHint}
                    error={slugAvailable === false ? 'Already taken — regenerate to get a new one.' : undefined}
                    action={<button type="button" className="brd-link brd-store__regen" onClick={regenerateSlug}><RefreshCw size={13} /> Regenerate</button>}>
                    <div className={`brd-money${slugAvailable === false ? ' brd-money--invalid' : ''} brd-store__slug`}>
                        <span className="brd-money__prefix">/store/</span>
                        <input value={form.store_slug} readOnly aria-label="Store link" />
                    </div>
                </StoreField>
                <StoreField label="Website URL">
                    <input className="brd-input" value={form.website_url} placeholder="https://yourbrand.com" aria-label="Website URL"
                        onChange={e => setForm((f: any) => ({ ...f, website_url: e.target.value }))} />
                </StoreField>
                <StoreField label="Store description" hint="Shown under your store name on the public page.">
                    <textarea className="brd-textarea" value={form.store_description} placeholder="Tell shoppers what your store is about" aria-label="Store description"
                        onChange={e => setForm((f: any) => ({ ...f, store_description: e.target.value }))} />
                </StoreField>
                <StoreField label="Default currency">
                    <div className="brd-chip-row" role="radiogroup" aria-label="Default currency">
                        {CURRENCIES.map(c => (
                            <button key={c} type="button" role="radio" aria-checked={form.default_currency === c}
                                className={`brd-chip${form.default_currency === c ? ' brd-chip--on' : ''}`}
                                onClick={() => setForm((f: any) => ({ ...f, default_currency: c }))}>
                                {c}
                            </button>
                        ))}
                    </div>
                </StoreField>
            </section>

            <section className="brd-section">
                <h2 className="brd-section__title">Branding</h2>
                <div className="brd-store-grid brd-store-grid--media">
                    <StoreImageUpload label="Logo" variant="logo" value={form.store_logo} uploadPath={uploadPath}
                        onChange={setLogo} onRemove={removeLogo} />
                    <StoreImageUpload label="Banner" variant="banner" value={form.store_banner} uploadPath={uploadPath}
                        onChange={setBanner} onRemove={removeBanner} />
                </div>
            </section>
        </>
    );

    // First-time setup: laid out like Create Brief — back + title, the form, a pinned footer.
    if (activeStep === 0) {
        return (
            <div className="brd brd-app brd-store">
                <div className="brd-page brd-store-form">
                    <div className="brd-container brd-container--narrow">
                        <header className="brd-page-head">
                            <button type="button" className="brd-icon-btn brd-icon-btn--filled" onClick={goBack} aria-label="Go back">
                                <ArrowLeft size={20} />
                            </button>
                            <div className="brd-page-head__text">
                                <h1 className="brd-page-head__title">Set up your storefront</h1>
                                <p className="brd-page-head__sub">Step 1 of {STEPS.length} · {STEPS[0]} — then you'll add products.</p>
                            </div>
                        </header>
                        {settingsSections}
                    </div>
                    <div className="brd-store-form__footer">
                        <div className="brd-container brd-container--narrow brd-store-form__footer-row">{saveButton}</div>
                    </div>
                </div>
            </div>
        );
    }

    // Returning brand: laid out like Campaigns — a white top block (title, action, tabs), the tab below.
    const publicUrl = `${BASE_URL_UI}/store/${form.store_slug}`;
    const copyLink = async () => { try { await navigator.clipboard.writeText(publicUrl); setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000); } catch {} };

    return (
        <div className="brd brd-app brd-store">
            <div className="brd-store-top">
                <div className="brd-container">
                    <header className="brd-store-top__head">
                        <button type="button" className="brd-icon-btn brd-icon-btn--filled" onClick={goBack} aria-label="Go back">
                            <ArrowLeft size={20} />
                        </button>
                        <div className="brd-store-top__heading">
                            <h1 className="brd-store-top__title">My Storefront</h1>
                            <p className="brd-store-top__link">
                                <span title={publicUrl}>{publicUrl.replace(/^https?:\/\//, '')}</span>
                                <button type="button" onClick={copyLink} aria-label="Copy store link" title={linkCopied ? 'Copied!' : 'Copy link'}>
                                    {linkCopied ? <Check size={14} className="brd-store__ok" /> : <Copy size={14} />}
                                </button>
                            </p>
                        </div>
                        <a className="brd-btn brd-btn--primary brd-store-top__cta" href={publicUrl} target="_blank" rel="noopener noreferrer">
                            <ExternalLink size={15} /> View Store
                        </a>
                    </header>
                    <div className="brd-tabs brd-store-tabs" role="tablist">
                        {tabs.map(({ label, icon }, i) => (
                            <button key={label} type="button" role="tab" aria-selected={settingsTab === i}
                                className={`brd-tabs__tab${settingsTab === i ? ' brd-tabs__tab--on' : ''}`}
                                onClick={() => { setSettingsTab(i); setAlert(null); }}>
                                {icon} {label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="brd-store__body">
                <div className="brd-container">
                    {settingsTab === 0 && <StorefrontProducts />}

                    {settingsTab === 1 && <StorefrontTraffic />}

                    {settingsTab === 2 && <StorefrontEarnings />}

                    {settingsTab === 3 && (
                        storefrontRow?.brand_deals_enabled === 'True'
                            ? <StorefrontCashkaroPerformance brand_id={brand_id} />
                            : dealsDisabledNote
                    )}

                    {settingsTab === 4 && (
                        <div className="brd-container--narrow">
                            {settingsSections}
                            <div className="brd-store__actions">{saveButton}</div>
                            <div style={{ marginTop: 24 }}>
                                {storefrontRow?.brand_deals_enabled === 'True' ? (
                                    <StorefrontCashkaroLink
                                        brand_id={brand_id}
                                        storefront_id={storefrontRow?.storefront_id}
                                        cashkaroCampaignId={storefrontRow?.cashkaro_campaign_id || null}
                                        cashkaroCampaignTitle={storefrontRow?.cashkaro_campaign_title || null}
                                        cashkaroCampaignThumbnail={storefrontRow?.cashkaro_campaign_thumbnail || null}
                                        latestCampaignRequest={storefrontRow?.latest_campaign_request || null}
                                        onLinked={refetchStorefront}
                                    />
                                ) : dealsDisabledNote}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: any) => ({ UserReducer: state.UserReducer, UserID: state.LoginUserIdReducer });
export const Storefront = connect(mapStateToProps)(StorefrontComponent);
